import { defaultSpecFor } from '@aurora/core'
import type { InteractiveKind, Level, SkillId } from '@aurora/core'
import { auroraStore } from '../store/auroraStore'

/**
 * Local demo seed.
 *
 * Fills the store directly with a real-shaped snapshot — no backend required.
 * This exists because local-first means the UI must be fully functional with
 * nothing but local state; if the app cannot render from IndexedDB alone, the
 * architecture is not actually local-first.
 *
 * The content is real: Aurora's actual post-cycle rubric, the principles that
 * came out of five weeks of work, and the catching stall the engine exists for.
 */

const DAY = 86_400_000
const now = Date.now()
const ago = (d: number) => now - d * DAY
const iso = (d: number) => new Date(now - d * DAY).toISOString().slice(0, 10)

const CHILD_ID = 'demo_child'
const THREAD_ID = 'demo_thread'

export function isLocalDemo(): boolean {
  try {
    return localStorage.getItem('aurora:localDemo') === '1'
  } catch {
    return false
  }
}

export function markLocalDemo() {
  try {
    localStorage.setItem('aurora:localDemo', '1')
  } catch {
    /* private mode — the demo just will not persist */
  }
}

export function seedLocalDemo() {
  const s = auroraStore.getState() as any
  s.__seedDemo?.() ?? applySeed()
  markLocalDemo()
}

function applySeed() {
  // Spine rows are derived from the same demo content the artifacts carry, so
  // the local demo exercises the PRIMARY read path (spine rows) exactly as a
  // production account does — zero duplication, one source of truth.
  const spine = deriveSpine()

  auroraStore.setState((prev: any) => ({
    ...prev,
    children: { [CHILD_ID]: child },
    threads: { [THREAD_ID]: thread },
    messages: Object.fromEntries(messages.map((m) => [m._id, m])),
    artifacts: Object.fromEntries(artifacts.map((a) => [a._id, a])),
    captures: Object.fromEntries(captures.map((c) => [c._id, c])),
    memories: Object.fromEntries(memories.map((m) => [m._id, m])),
    skills: spine.skills,
    skillStates: spine.skillStates,
    cycles: spine.cycles,
    weeks: spine.weeks,
    days: spine.days,
    blocks: spine.blocks,
    ui: {
      ...prev.ui,
      activeChildId: CHILD_ID,
      activeThreadId: THREAD_ID,
      view: 'thread',
      hydrated: true,
    },
  }), true)
}

function deriveSpine() {
  const skills: Record<string, any> = {}
  const skillStates: Record<string, any> = {}
  for (const d of rubricData.domains as any[]) {
    for (const sk of d.skills ?? []) {
      const key = `${d.key}.${sk.key}`
      skills[`skill_${key}`] = {
        _id: `skill_${key}`,
        childId: CHILD_ID,
        key,
        domainKey: d.key,
        domainName: d.name,
        name: sk.name,
        source: 'built_in',
        updatedAt: now,
      }
      if (typeof sk.level === 'number') {
        skillStates[`state_${key}`] = {
          _id: `state_${key}`,
          childId: CHILD_ID,
          skillKey: key,
          level: sk.level,
          confidence: sk.confidence ?? 0.5,
          trend: sk.trend ?? 'steady',
          note: sk.note,
          evidenceCaptureIds: [],
          observationCount: Math.round((sk.confidence ?? 0.5) * 8),
          lastObservedAt: ago(3),
          stallSince: sk.trend === 'stalled' ? ago(35) : undefined,
          updatedAt: now,
        }
      }
    }
  }

  const cycles: Record<string, any> = {
    demo_cycle: {
      _id: 'demo_cycle',
      childId: CHILD_ID,
      number: 2,
      title: planData.title,
      startDate: iso(2),
      endDate: iso(-40),
      goals: [
        { statement: 'Cross the fluency wall — read an easy book solo and want to do it again', domainKey: 'literacy' },
        { statement: 'Push reasoning past where it stands: 4-clue deduction, 3x3 matrices', domainKey: 'logic' },
        { statement: 'Writing fused into drawing — invented spelling, her own books', domainKey: 'fineMotor' },
      ],
      principles: [
        'Ride her interests — attention is content-gated',
        'Writing rides drawing and reading, never a drill',
        'Easier text, more repetition — that is what builds fluency',
      ],
      status: 'active',
      updatedAt: now,
    },
  }

  const weeks: Record<string, any> = {}
  const days: Record<string, any> = {}
  const blocks: Record<string, any> = {}
  for (const w of (planData as any).weeks ?? []) {
    const weekId = `demo_week_${w.number}`
    const dates: string[] = (w.days ?? []).map((d: any) => d.date).filter(Boolean).sort()
    weeks[weekId] = {
      _id: weekId,
      childId: CHILD_ID,
      cycleId: 'demo_cycle',
      number: w.number,
      theme: w.theme,
      startDate: dates[0] ?? iso(2),
      updatedAt: now,
    }
    for (const d of w.days ?? []) {
      const dayId = `demo_day_${d.date}`
      days[dayId] = {
        _id: dayId,
        childId: CHILD_ID,
        weekId,
        date: d.date,
        note: d.note,
        updatedAt: now,
      }
      let slot = 1
      for (const b of d.blocks ?? []) {
        const blockId = b.id ?? `demo_block_${d.date}_${slot}`
        blocks[blockId] = {
          _id: blockId,
          childId: CHILD_ID,
          dayId,
          date: d.date,
          slot: slot++,
          title: b.title,
          instructions: b.instructions,
          why: b.why,
          minutes: b.minutes ?? 10,
          domainKey: b.domain,
          status: b.status ?? 'pending',
          updatedAt: now,
        }
      }
    }
  }

  return { skills, skillStates, cycles, weeks, days, blocks }
}

// ── the child ────────────────────────────────────────────────

const child = {
  _id: CHILD_ID,
  name: 'Aurora',
  birthDate: iso(1005), // ~2y9m
  pronouns: { subject: 'she', object: 'her', possessive: 'her' },
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  updatedAt: now,
}

const thread = {
  _id: THREAD_ID,
  childId: CHILD_ID,
  title: 'Cycle 2 planning',
  lastMessageAt: ago(0),
  state: 'Reading fluency is the focus; catching needs a new approach',
  updatedAt: now,
}

// ── memory: the principles that actually came out of Cycle 1 ─

const memories = [
  {
    _id: 'mem_1', childId: CHILD_ID, kind: 'principle', always: true, weight: 0.95,
    statement: 'Attention is content-gated',
    detail:
      'Across five weeks, focus stayed long on novel or interesting work and short on everything else. Time-on-task never generalized from one to the other.',
    status: 'active', createdAt: ago(30), updatedAt: ago(30),
  },
  {
    _id: 'mem_2', childId: CHILD_ID, kind: 'principle', always: true, weight: 0.9,
    statement: 'Isolated letter drills get skipped',
    detail:
      'Standalone handwriting was the most-skipped block type. She reliably chooses drawing over letter formation, so writing has to ride on drawing or reading.',
    status: 'active', createdAt: ago(28), updatedAt: ago(28),
  },
  {
    _id: 'mem_3', childId: CHILD_ID, kind: 'principle', always: true, weight: 0.88,
    statement: 'For reading, easier is faster',
    detail:
      'She decodes accurately but slowly. The bottleneck is automaticity, not skill — volume at 95% known words builds fluency where harder books stall it.',
    status: 'active', createdAt: ago(21), updatedAt: ago(21),
  },
  {
    _id: 'mem_4', childId: CHILD_ID, kind: 'principle', weight: 0.82,
    statement: 'Caregiving is a learning channel',
    detail:
      'She is notably gentle with younger children and engages far longer when teaching someone else. Reading to a doll turns repetition into play.',
    status: 'active', createdAt: ago(14), updatedAt: ago(14),
  },
  {
    _id: 'mem_5', childId: CHILD_ID, kind: 'interest', weight: 0.8,
    statement: 'Into detectives and mysteries right now',
    status: 'active', createdAt: ago(9), updatedAt: ago(2),
  },
  {
    _id: 'mem_6', childId: CHILD_ID, kind: 'interest', weight: 0.6,
    statement: 'Dinosaurs, on and off',
    status: 'active', createdAt: ago(20), updatedAt: ago(6),
  },
]

// ── the rubric artifact ──────────────────────────────────────

const rubricData = {
  domains: [
    {
      key: 'literacy', name: 'Literacy & Reading',
      skills: [
        { key: 'letterSounds', name: 'Letter sounds', level: 5, confidence: 0.9, trend: 'steady' },
        { key: 'blending', name: 'Blending', level: 5, confidence: 0.9, trend: 'steady' },
        { key: 'decodingAccuracy', name: 'Decoding accuracy', level: 5, confidence: 0.85, trend: 'steady',
          note: 'Reads "snacks" and "swift" correctly' },
        { key: 'decodingFluency', name: 'Reading fluency', level: 3, confidence: 0.8, trend: 'rising',
          note: 'Accurate but slow — this is the wall' },
        { key: 'sightWords', name: 'Sight-word automaticity', level: 3, confidence: 0.75, trend: 'rising' },
        { key: 'independentReading', name: 'Reading a book alone', level: 2, confidence: 0.8, trend: 'rising' },
      ],
    },
    {
      key: 'logic', name: 'Logic & Reasoning',
      skills: [
        { key: 'opposites', name: 'Opposites', level: 5, confidence: 0.9, trend: 'steady' },
        { key: 'causeEffect', name: 'Cause and effect', level: 5, confidence: 0.9, trend: 'steady',
          note: 'Chains 3-4 steps unprompted' },
        { key: 'oddOneOut', name: 'Odd one out', level: 5, confidence: 0.85, trend: 'steady' },
        { key: 'analogies', name: 'Analogies', level: 4, confidence: 0.7, trend: 'rising' },
        { key: 'deduction', name: 'Deduction', level: 3, confidence: 0.7, trend: 'rising' },
        { key: 'matrix', name: 'Matrix reasoning', level: 3, confidence: 0.65, trend: 'rising',
          note: '2x2 solid, 3x3 emerging' },
        { key: 'classification', name: 'Two-attribute sorting', level: 2, confidence: 0.6, trend: 'stalled',
          note: 'Fails as an abstract sort; worth trying as a hunt instead' },
      ],
    },
    {
      key: 'creative', name: 'Creative Expression',
      skills: [
        { key: 'freeDrawing', name: 'Drawing', level: 5, confidence: 0.9, trend: 'steady',
          note: 'Draws cats and houses; draws on everything' },
        { key: 'observational', name: 'Drawing from life', level: 2, confidence: 0.6, trend: 'rising' },
        { key: 'faceProfile', name: 'Profile faces', level: 1, confidence: 0.5, trend: 'steady' },
        { key: 'building', name: 'Building', level: 4, confidence: 0.7, trend: 'steady' },
      ],
    },
    {
      key: 'fineMotor', name: 'Fine Motor & Writing',
      skills: [
        { key: 'grip', name: 'Pencil grip', level: 5, confidence: 0.9, trend: 'steady' },
        { key: 'uppercase', name: 'Uppercase letters', level: 4, confidence: 0.8, trend: 'rising' },
        { key: 'lowercase', name: 'Lowercase letters', level: 1, confidence: 0.5, trend: 'steady' },
        { key: 'inventedSpelling', name: 'Invented spelling', level: 2, confidence: 0.5, trend: 'rising' },
      ],
    },
    {
      key: 'grossMotor', name: 'Gross Motor',
      skills: [
        { key: 'jumping', name: 'Jumping', level: 5, confidence: 0.85, trend: 'steady' },
        { key: 'kicking', name: 'Kicking', level: 5, confidence: 0.85, trend: 'steady' },
        { key: 'catching', name: 'Catching', level: 1, confidence: 0.9, trend: 'stalled',
          note: 'Six attempts across five weeks with no movement. Hands are not closing in time — a timing problem, not effort.' },
        { key: 'balance', name: 'Balance', level: 4, confidence: 0.7, trend: 'steady' },
      ],
    },
    {
      key: 'socialEmotional', name: 'Social & Emotional',
      skills: [
        { key: 'caregiving', name: 'Caring for others', level: 5, confidence: 0.85, trend: 'rising',
          note: 'Notably gentle and aware with younger cousins' },
        { key: 'empathy', name: 'Empathy', level: 5, confidence: 0.85, trend: 'steady' },
        { key: 'regulation', name: 'Handling frustration', level: 5, confidence: 0.8, trend: 'steady' },
        { key: 'cooperative', name: 'Playing with others', level: 2, confidence: 0.6, trend: 'steady',
          note: 'Limited peer exposure — hard to move at home' },
      ],
    },
    {
      key: 'executiveFunction', name: 'Attention & Focus',
      skills: [
        { key: 'attentionPreferred', name: 'Focus on what she likes', level: 5, confidence: 0.85, trend: 'steady' },
        { key: 'attentionOther', name: 'Focus on everything else', level: 2, confidence: 0.85, trend: 'steady',
          note: 'Did not generalize over five weeks. Ride interests instead of drilling.' },
        { key: 'memory', name: 'Working memory', level: 4, confidence: 0.7, trend: 'rising' },
      ],
    },
    { key: 'math', name: 'Mathematics', skills: [
      { key: 'counting', name: 'Counting', level: 4, confidence: 0.8, trend: 'rising' },
      { key: 'patterns', name: 'Patterns', level: 4, confidence: 0.8, trend: 'rising', note: 'AB and AAB solid' },
      { key: 'addition', name: 'Adding', level: 2, confidence: 0.6, trend: 'rising' },
    ]},
    { key: 'language', name: 'Language', skills: [
      { key: 'vocabulary', name: 'Vocabulary', level: 5, confidence: 0.85, trend: 'steady' },
      { key: 'sentences', name: 'Sentences', level: 5, confidence: 0.8, trend: 'steady' },
      { key: 'stories', name: 'Telling stories', level: 4, confidence: 0.7, trend: 'rising' },
    ]},
    { key: 'time', name: 'Time', skills: [
      { key: 'daysOfWeek', name: 'Days of the week', level: 3, confidence: 0.6, trend: 'rising' },
      { key: 'sequence', name: 'Before and after', level: 3, confidence: 0.6, trend: 'rising' },
    ]},
    { key: 'science', name: 'Science & Nature', skills: [
      { key: 'prediction', name: 'Predicting', level: 4, confidence: 0.65, trend: 'rising' },
    ]},
    { key: 'secondLanguage', name: 'Russian', skills: [
      { key: 'receptive', name: 'Understanding', level: 2, confidence: 0.6, trend: 'rising' },
      { key: 'speaking', name: 'Speaking', level: 1, confidence: 0.6, trend: 'steady',
        note: 'Repeats readily; nothing unprompted yet, which is normal at this stage' },
    ]},
    { key: 'selfCare', name: 'Self-Care', skills: [
      { key: 'dressing', name: 'Dressing', level: 4, confidence: 0.75, trend: 'steady' },
      { key: 'eating', name: 'Eating', level: 4, confidence: 0.75, trend: 'steady' },
    ]},
  ],
}

// ── the plan artifact ────────────────────────────────────────

const planData = {
  title: 'Cycle 2 — reading fluency and deeper logic',
  weeks: [
    {
      number: 1,
      theme: 'Detectives & Mysteries',
      days: [
        {
          date: iso(0),
          blocks: [
            {
              id: 'b1', title: 'Sight words, then echo read', domain: 'literacy', minutes: 10, status: 'pending',
              artifactIds: ['art_sightwords'],
              why: 'Instant recognition of common words is the single biggest unlock for speed.',
              instructions:
                'Two minutes on the sight-word cards — instant recognition only, no sounding out. Then one very easy book: you read a line with expression, she echoes it back.',
            },
            {
              id: 'b2', title: 'Who took the cookie?', domain: 'logic', minutes: 10, status: 'pending',
              artifactIds: ['art_deduction'],
              why: 'Deduction with negation is the next rung above the cause-and-effect chains she already finds easy.',
              instructions:
                'Three stuffed animals. "Someone took the cookie. The thief is NOT wearing red. The thief is NOT the biggest. Who was it?" Three rounds, getting harder. Ask how she knew.',
            },
            {
              id: 'b3', title: 'Catching, back to the beginning', domain: 'grossMotor', minutes: 10, status: 'pending',
              why: 'Five weeks of practice did not move this, so it is timing, not effort. Build the reflex before adding any distance.',
              instructions:
                'Forget the tennis ball. Balloon or soft fabric ball, two feet away, underhand, almost placing it in her hands. Ten easy catches. The only goal is that her hands close on contact.',
            },
          ],
        },
        {
          date: iso(-1),
          blocks: [
            {
              id: 'b4', title: 'Re-read and beat your time', domain: 'literacy', minutes: 10, status: 'pending',
              why: 'Repeated reading of easy text is the best-evidenced way through a fluency plateau.',
              instructions:
                'Same book as yesterday. Time her, then let her try to beat it. Three reads is plenty. Celebrate the drop in time, not the accuracy.',
            },
            {
              id: 'b5', title: 'Find the one that is small AND blue', domain: 'logic', minutes: 10, status: 'pending',
              artifactIds: ['art_hunt'],
              why: 'Two attributes at once, framed as a hunt — the abstract sorting version has not landed, so we changed the shape.',
              instructions: 'Six toys differing in size and colour. Ask her to hunt for the one that is small AND blue.',
            },
          ],
        },
      ],
    },
  ],
}

/**
 * Playable materials.
 *
 * Built through `defaultSpecFor` rather than hand-written, so every one of
 * them is a spec the validator actually accepts — which is the point, since
 * Kid Mode refuses anything else. Titles and themes are overridden to match
 * the week the demo is in.
 */
function material(
  id: string,
  kind: InteractiveKind,
  difficulty: Level,
  input: { title: string; summary: string; theme?: string; skills: string[]; days: number; played?: number },
) {
  return {
    _id: id,
    childId: CHILD_ID,
    kind: 'activity',
    version: 1,
    isCurrent: true,
    title: input.title,
    summary: input.summary,
    source: 'agent',
    tags: [kind],
    spec: {
      ...defaultSpecFor(kind, difficulty, {
        theme: input.theme,
        targetSkillIds: input.skills as SkillId[],
      }),
      title: input.title,
    },
    generation: { prompt: input.summary, model: 'demo', status: 'ready', requestedAt: ago(input.days) },
    playCount: input.played,
    lastPlayedAt: input.played ? ago(input.days - 1) : undefined,
    createdAt: ago(input.days),
    updatedAt: ago(input.days),
  }
}

const materials = [
  material('art_deduction', 'deduction_puzzle', 3, {
    title: 'Who took the cookie?',
    summary: 'Three clues with negation — the rung above the cause-and-effect chains she finds easy.',
    theme: 'dinosaurs',
    skills: ['deduction'],
    days: 1,
  }),
  material('art_matrix', 'matrix_reasoning', 3, {
    title: 'Find the missing square',
    summary: 'A 3 by 3 grid with one cell blank. 2 by 2 is solid, so this is the next step.',
    skills: ['matrix', 'analogies'],
    days: 2,
    played: 2,
  }),
  material('art_sightwords', 'sight_word_deck', 3, {
    title: 'The words she still stops on',
    summary: 'Instant recognition only, no sounding out — the biggest single unlock for speed.',
    skills: ['sightWords', 'decodingFluency'],
    days: 3,
    played: 4,
  }),
  material('art_hunt', 'sorting_game', 2, {
    title: 'Hunt for the small blue one',
    summary: 'Two attributes at once, framed as a hunt. The abstract sort never landed; this shape might.',
    skills: ['classification'],
    days: 2,
  }),
]

const artifacts = [
  {
    _id: 'art_rubric', childId: CHILD_ID, kind: 'rubric', version: 3, isCurrent: true,
    title: 'Where Aurora is — end of Cycle 1',
    summary: 'Logic and creative are standouts; reading is at a fluency wall; catching is stuck.',
    data: rubricData, source: 'agent', tags: ['rubric'],
    createdAt: ago(4), updatedAt: ago(4),
  },
  {
    _id: 'art_plan', childId: CHILD_ID, kind: 'plan', version: 1, isCurrent: true,
    title: 'Cycle 2 — reading fluency and deeper logic',
    summary: 'Six weeks. Reading and logic as daily anchors, ten minutes each.',
    data: planData, source: 'agent', tags: ['plan'],
    createdAt: ago(3), updatedAt: ago(3),
  },
  {
    _id: 'art_guide', childId: CHILD_ID, kind: 'document', version: 1, isCurrent: true,
    title: 'Crossing the reading fluency wall',
    summary: 'Why she is slow despite decoding well, and the five levers that actually work.',
    body:
      '## The wall, in plain terms\n\nDecoding still eats all her attention — she is sounding out one letter at a time, like a beginner playing piano note by note. That is too effortful to leave room for speed or for enjoying the story.\n\nFluency arrives when she has "played the song" enough times that her eyes simply know the word. You get there with volume and repetition at an **easy** level, not with harder books.\n\n## The levers, in order\n\n1. **Easy text.** Books where she already knows ~95% of the words. Success builds speed; struggle builds avoidance.\n2. **Sight-word automaticity.** The top hundred words are about half of all text. Instant recognition roughly doubles her speed.\n3. **Repeated reading.** The same book three or four times. Time it — "beat your own time" already works for her on agility courses.\n4. **Chunking.** Teach `sh`, `ch`, `th`, and word families as single units rather than letters.\n5. **Protect the love.** Keep reading *to* her above her level, so stories stay magical while her own reading is still work.\n\n## The hook that fits her\n\nHave her read to an audience — the dolls, the baby, you. Performing an easy, familiar book is a joy she will repeat endlessly, and that repetition is exactly what builds fluency. It rides her caregiving instinct instead of fighting her attention.',
    source: 'agent', tags: ['reading', 'method'],
    createdAt: ago(6), updatedAt: ago(6),
  },
  ...materials,
]

// ── captures + thread ────────────────────────────────────────

const captures = [
  {
    _id: 'cap_1', childId: CHILD_ID, occurredAt: ago(1), capturedAt: ago(1), source: 'voice',
    raw: { transcript: 'She did the matrix puzzle — got the 2 by 2 right away, needed help on the 3 by 3 but she figured out the colour rule herself. Said "the blue one goes down here".' },
    interpretation: {
      summary: 'Solved 2x2 matrix independently; worked out the colour dimension on 3x3 with support.',
      domains: ['logic'],
      quotes: ['the blue one goes down here'],
      affect: 'engaged',
    },
    updatedAt: ago(1),
  },
  {
    _id: 'cap_2', childId: CHILD_ID, occurredAt: ago(2), capturedAt: ago(2), source: 'voice',
    raw: { transcript: 'Catching still is not working. Ten tries with the tennis ball, zero catches. Her hands close after the ball has already gone past.' },
    interpretation: {
      summary: 'Catching unchanged. Hands closing late — a timing problem rather than effort or strength.',
      domains: ['grossMotor'],
      quotes: [],
      affect: 'neutral',
    },
    updatedAt: ago(2),
  },
  {
    _id: 'cap_3', childId: CHILD_ID, occurredAt: ago(3), capturedAt: ago(3), source: 'voice',
    raw: { transcript: 'She read the whole Bob book by herself and then wanted to do it again. Then she read it to her bear.' },
    interpretation: {
      summary: 'First fully independent read of an easy book, followed by a spontaneous re-read and reading aloud to a toy.',
      domains: ['literacy', 'socialEmotional'],
      quotes: [],
      affect: 'delighted',
    },
    updatedAt: ago(3),
  },
]

const messages = [
  {
    _id: 'msg_1', threadId: THREAD_ID, childId: CHILD_ID, role: 'user',
    content: 'Catching still is not working. Five weeks now. What do we do?',
    createdAt: ago(1) - 3600_000, updatedAt: ago(1),
  },
  {
    _id: 'msg_2', threadId: THREAD_ID, childId: CHILD_ID, role: 'assistant',
    content:
      'Stop practising catching and reset the mechanics.\n\nI looked back at six attempts across five weeks — accuracy has not moved at all, and your note from yesterday is the tell: *"her hands close after the ball has already gone past."* That is not a strength or an effort problem. It is anticipatory timing, and more repetitions at the same difficulty will not build it.\n\n**What I would do instead**\n\n- Drop to a balloon or a soft fabric ball. Slower flight gives her time to see it coming.\n- Two feet away, underhand, almost placing it into her hands.\n- Ten easy successes. The only thing you are training is that her hands close *on contact*.\n- Add distance only once that is automatic — and go back a step the moment it is not.\n\nThis is the same move that is working for reading: drop the difficulty far enough that success is guaranteed, then let volume do the work.\n\nI have swapped the block in tomorrow\'s plan to the reset version.',
    toolCalls: [
      { id: 't1', name: 'search', input: { query: 'catching' }, status: 'complete' },
      { id: 't2', name: 'write_artifact', input: { kind: 'plan', title: 'Cycle 2 — reading fluency and deeper logic' }, status: 'complete' },
    ],
    attachments: [{ kind: 'artifact', artifactId: 'art_plan' }],
    createdAt: ago(1) - 3500_000, updatedAt: ago(1),
  },
  {
    _id: 'msg_3', threadId: THREAD_ID, childId: CHILD_ID, role: 'user',
    content: 'She read a whole Bob book by herself today and then read it to her bear.',
    createdAt: ago(0) - 7200_000, updatedAt: ago(0),
  },
  {
    _id: 'msg_4', threadId: THREAD_ID, childId: CHILD_ID, role: 'assistant',
    content:
      'That is the wall breaking. Worth marking the date.\n\nTwo things happened there, and the second one matters more than it looks. Reading it *once* is decoding. Wanting to read it *again*, and then reading it to her bear, is the point where reading stops being work and starts being something she does for her own reasons. That is the switch fluency needs — the repetition stops being something you ask for.\n\nI have moved independent reading up and noted that reading-to-an-audience is now self-initiating rather than something we have to set up.\n\n**What I would do with this**\n\nKeep the difficulty exactly where it is for another week or two. The instinct now is to hand her something harder — resist it. Let her rack up easy wins at this level until it is genuinely effortless, then step up.\n\nAlso: let her keep that book. A book she can read start to finish is worth more than five she cannot.',
    toolCalls: [
      { id: 't3', name: 'remember', input: { kind: 'signal', statement: 'Reading to an audience is now self-initiated' }, status: 'complete' },
      { id: 't4', name: 'write_artifact', input: { kind: 'rubric', title: 'Where Aurora is' }, status: 'complete' },
    ],
    attachments: [{ kind: 'artifact', artifactId: 'art_rubric' }],
    createdAt: ago(0) - 7100_000, updatedAt: ago(0),
  },
]
